import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { List, Spin } from "antd";
import { OrderShipmentItem } from "./order-shipment";
import { BulkCancelPendingPanel } from "./BulkCancelPendingPanel";

interface DiscountIconProps {
  disnumber?: any;
  priceInclTax?: any;
  price?: any;
}

interface SelectedItems {
  [shipmentId: string]: any[];
}

type Props = {
  products?: any;
  items?: any;
  invoice?: string;
  updateOrderData?: () => void;
  canEnableDelivery?: boolean;
  orderPayment?: any;
  discountAmount?: number;
  interests?: number;
  additionalPayment?: number;
  orderId?: number;
  allShipments?: any;
  optimizations?: any[];
  orderItems?: any[];
  /** URL base de la tienda Magento del vendedor (ej: "https://www.materialessur.com.ar/").
   *  Se usa para construir la URL de descarga de etiqueta Zippin. */
  storeLink?: string;
  creditMemos?: any[];
  /** XCONS-1929 bulk cancel: 'off' = listado normal, 'select' = etapa 1
   * (selección bulk preselección). 'preview' se deriva del backend. */
  bulkCancelMode?: "off" | "select";
  /** Callback cuando el panel bulk decide salir (Back en etapa 1, o
   * después de un Reject persistido). */
  onBulkCancelExit?: () => void;
};

export const OrderShipment = ({
  products,
  items,
  invoice,
  updateOrderData,
  canEnableDelivery,
  orderPayment,
  discountAmount,
  interests,
  additionalPayment,
  orderId,
  allShipments,
  optimizations,
  orderItems,
  storeLink,
  creditMemos,
  bulkCancelMode = "off",
  onBulkCancelExit,
}: Props) => {
  const statusDelivery = [
    "pending",
    "preparing",
    "ready_to_deliver",
    "in_transit",
    "delivered",
    "pending_cancel",
    "pending_move",
    "canceled",
  ];

  const productsMap = useMemo(() => {
    const map: Record<string, any> = {};
    products?.forEach((p: any) => (map[p.itemId] = p));
    return map;
  }, [products]);

  // Cuando una entrega entra en modo edición (move/cancel), las demás se
  // dimean al 70% para guiar la atención y prevenir interacción. Spec
  // XCONS-1929 etapa 1 + tickets relacionados.
  const [editingShipmentId, setEditingShipmentId] = useState<number | null>(
    null,
  );

  // Detecta si hay un grupo bulk activo en backend: shipments con
  // pending_cancel + bulkCancelRequest.groupId. Si lo hay, se renderiza el
  // panel bulk (etapa preview) en lugar del listado normal.
  // También se considera un grupo "committed" (todos canceled con el
  // mismo groupId) que se renderiza como "All pending deliveries cancelled".
  const bulkGroupInfo = useMemo(() => {
    const list = (items ?? []) as any[];
    const byGroup: Record<string, { pending: any[]; canceled: any[] }> = {};
    list.forEach((sh) => {
      const gid = (sh?.metadata as any)?.bulkCancelRequest?.groupId;
      if (!gid) return;
      if (!byGroup[gid]) byGroup[gid] = { pending: [], canceled: [] };
      if (sh.status === "pending_cancel") byGroup[gid].pending.push(sh);
      else if (sh.status === "canceled") byGroup[gid].canceled.push(sh);
    });
    // Toma el primer grupo (caso común: 1 grupo activo). Casos múltiples
    // quedan para una iteración futura.
    const entries = Object.entries(byGroup);
    if (entries.length === 0) return null;
    const [gid, group] = entries[0];
    return {
      groupId: gid,
      pendingShipments: group.pending,
      canceledShipments: group.canceled,
      stage:
        group.pending.length > 0
          ? ("preview" as const)
          : ("committed" as const),
    };
  }, [items]);

  // Las entregas canceladas se muestran al final del listado, sin alterar
  // el orden relativo del resto (sort estable).
  const sortedItems = useMemo(() => {
    const list = (items ?? []) as any[];
    return [...list].sort((a, b) => {
      const aCanceled = a?.status === "canceled" ? 1 : 0;
      const bCanceled = b?.status === "canceled" ? 1 : 0;
      return aCanceled - bCanceled;
    });
  }, [items]);

  // Llegar con `#shipment-panel-<id>` (el link "Entrega N" del historial de otra pantalla,
  // xcons-docs#583) lleva a esa entrega, desde su título. Una sola vez por hash, para no
  // tironear el scroll en cada refetch.
  //
  // No alcanza con que el panel exista: aparece mientras lo de arriba (pagos, facturas) todavía
  // carga, y cuando eso se acomoda el panel sube varios cientos de px. Saltar en ese momento
  // deja la página pasada de largo. Se espera a que su posición se quede quieta.
  const { hash } = useLocation();
  const hashScrolled = useRef<string | null>(null);
  useEffect(() => {
    if (!hash.startsWith("#shipment-panel-") || !items || hashScrolled.current === hash) return;
    if (!document.getElementById(hash.slice(1))) return;
    const posicion = () => {
      const el = document.getElementById(hash.slice(1));
      return el ? Math.round(el.getBoundingClientRect().top + window.scrollY) : null;
    };
    let anterior = posicion();
    let quieta = 0;
    let vueltas = 0;
    const timer = window.setInterval(() => {
      const actual = posicion();
      quieta = actual !== null && actual === anterior ? quieta + 1 : 0;
      anterior = actual;
      vueltas += 1;
      // ~450 ms sin moverse, o 4 s como techo: un pedido que nunca se queda quieto igual llega.
      if (quieta >= 3 || vueltas >= 27) {
        window.clearInterval(timer);
        hashScrolled.current = hash;
        document
          .getElementById(hash.slice(1))
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 150);
    return () => window.clearInterval(timer);
  }, [hash, items]);

  // Cuando estoy en modo bulk (select desde menu, preview desde backend, o
  // committed con grupo cancelado), muestro el panel bulk completo.
  const showBulkPanel =
    bulkCancelMode === "select" ||
    bulkGroupInfo?.stage === "preview" ||
    bulkGroupInfo?.stage === "committed";

  if (showBulkPanel) {
    return (
      <Spin spinning={!items}>
        <BulkCancelPendingPanel
          items={items ?? []}
          productsMap={productsMap}
          orderId={orderId}
          updateOrderData={updateOrderData}
          mode={
            bulkGroupInfo?.stage === "committed"
              ? "committed"
              : bulkGroupInfo?.stage === "preview"
                ? "preview"
                : "select"
          }
          bulkGroup={bulkGroupInfo ?? undefined}
          onExitSelectMode={() => onBulkCancelExit?.()}
        />
      </Spin>
    );
  }

  return (
    <Spin spinning={!items}>
      <List
        className="summary-list"
        itemLayout="vertical"
        size="large"
        dataSource={sortedItems}
        renderItem={(shipment: any, index: number) => {
          const isDimmed =
            editingShipmentId !== null &&
            editingShipmentId !== shipment.shipmentId;
          return (
            <div id={`shipment-panel-${shipment.shipmentId}`} key={shipment.shipmentId}>
            <OrderShipmentItem
              key={shipment.shipmentId}
              shipment={shipment}
              productsMap={productsMap}
              statusDelivery={statusDelivery}
              invoice={invoice}
              updateOrderData={updateOrderData}
              canEnableDelivery={canEnableDelivery}
              orderPayment={orderPayment}
              discountAmount={discountAmount}
              interests={interests}
              additionalPayment={additionalPayment}
              orderId={orderId}
              allShipments={allShipments}
              optimizations={optimizations}
              orderItems={orderItems}
              storeLink={storeLink}
              creditMemos={creditMemos}
              isDimmed={isDimmed}
              onEnterEditMode={() =>
                setEditingShipmentId(shipment.shipmentId)
              }
              onExitEditMode={() => setEditingShipmentId(null)}
            />
            </div>
          );
        }}
      />
    </Spin>
  );
};
